import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import clsx from "clsx";
import { NumericFormat } from "react-number-format";
import FormDate from "../../../v2/Form/FormDate";
import FormInput from "../../../v2/Form/FormInput";
import FormSelect from "../../../v2/Form/FormSelect";
import {
  DownloadIcon,
  DocumentIcon,
  PencilIcon,
  ArrowLeftIcon,
  XIcon,
} from "@heroicons/react/outline";
import { downloadData, getUrl } from "aws-amplify/storage";
import SlideOver from "../../Modal/SlideOver";
import TransactionDetailModal from "../TransactionDetailModal";
import PaymentHistoryList from "../../../PaymentHistoryList";
import DepositApplyPanel from "../DepositApplyPanel";
import DepositDetailModal from "../DepositDetailModal";
import {
  addLocalNotification,
  cancelDepositUsage,
  clearAccountingTransaction,
  clearAcctTransactionError,
  clearDownloadInvoice,
  requestCreateAcctTransaction,
  requestDownloadInvoice,
  requestGetAccountingTransaction,
  requestListAccount,
  requestListBusinessTax,
  listDeposits,
  refreshAccountingTransaction,
  useAccountingDispatch,
  useAccountingState,
  useBusinessTaxDispatch,
  useBusinessTaxState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import { TAX_DIRECTORY_LIMIT } from "../../../../Constants/taxCategories";
import {
  currencyFormat,
  formatDateTime,
  getAccountingFileName,
  getPeriodClosedError,
} from "../../../../Utils";
import PeriodClosedNotice from "../PeriodClosedNotice";
import PeriodLockCell from "../PeriodLockCell";
import {
  calculateItemTaxBreakdown,
  describeTaxRow,
  getCashBankOptions,
  senDecimalProps,
} from "../../../../Utils/AccountingUtil";
import { useInvoiceTotals } from "../../../../Hooks/useInvoiceTotals";
import { downloadFakturKeluaranXml } from "../../../../Utils/FakturXmlUtil";
import FakturXmlExportModal from "../FakturXmlExportModal";
import { fromPeriod } from "../../../../Utils/calendarDate";
import {
  getDepositActionError,
  isDepositApplication,
  isDepositOpen,
} from "../../../../Utils/DepositUtil";
import { isTransactionPaid } from "../../../../Utils/PaymentUtils";
import FormError from "../../Form/FormError";

const SummaryRow = ({ label, value, bold, colorClass }) => (
  <div
    className={`flex justify-between text-sm ${bold ? "font-semibold" : ""} ${colorClass || ""}`}
  >
    <span>{label}</span>
    <NumericFormat
      value={value || 0}
      {...senDecimalProps(value || 0)}
      thousandSeparator="."
      decimalSeparator=","
      displayType="text"
      prefix="Rp"
    />
  </div>
);

// Sen, when there are any, print both digits: Rp0,10, not Rp0,1.
const formatRupiah = (amount) =>
  `Rp${new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: senDecimalProps(amount).fixedDecimalScale ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount || 0)}`;

const InvoiceDetailModal = ({
  open,
  onClose,
  onEdit,
  onPaySuccess,
  invoice: invoiceProp,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const {
    downloadInvoice,
    downloadInvoiceLoading,
    downloadInvoiceError,
    accounts,
    accountLoading,
    allAccountLoading,
    submitLoading,
    submitError,
    transactionID,
    transactions,
    transaction: fetchedTransaction,
    loading: transactionDetailLoading,
  } = useAccountingState();

  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const downloadRef = useRef(null);
  const [showXmlModal, setShowXmlModal] = useState(false);

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isTransactionDetailOpen, setIsTransactionDetailOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [totalPaid, setTotalPaid] = useState("");
  const [paymentDescription, setPaymentDescription] = useState("");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [dateError, setDateError] = useState("");
  const [totalError, setTotalError] = useState("");
  const [selectedAccountError, setSelectedAccountError] = useState("");
  // Set when the AR/AP control account can't be resolved from the invoice — the
  // payment has no second leg then, so it must not be submitted.
  const [controlAccountError, setControlAccountError] = useState("");

  // Re-read after a deposit is applied or undone, for an invoice that is not
  // in the page's list (opened from a report link, say).
  const [freshInvoice, setFreshInvoice] = useState(null);

  // Always use the live version from context so reducer updates (e.g. after payment) are reflected
  const invoice = useMemo(
    () =>
      invoiceProp
        ? (transactions.find((t) => t.id === invoiceProp.id) ??
          (freshInvoice?.id === invoiceProp.id ? freshInvoice : invoiceProp))
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoiceProp?.id, transactions, freshInvoice],
  );

  // Purchase (Bill) settles a payable; sale settles a receivable.
  const isPurchase = ["EXPENSE_PAYABLE", "PAYABLE"].includes(invoice?.type);

  // Sales deposits (W5–W7). Only a sale can use them, and only the customer's
  // own deposits that still have something left.
  const [openDeposits, setOpenDeposits] = useState([]);
  const [isApplyOpen, setIsApplyOpen] = useState(false);
  // The applications just made here, so the banner can undo them together.
  const [appliedUsages, setAppliedUsages] = useState([]);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoError, setUndoError] = useState("");
  const [depositDetailID, setDepositDetailID] = useState(null);

  const depositPartnerID = !isPurchase ? invoice?.partnerID : null;
  // Only the latest request may write: switching from customer A's invoice to
  // B's while A's deposits are still loading must not show A's offer on B.
  const depositsRequest = useRef(0);
  const loadOpenDeposits = async () => {
    depositsRequest.current += 1;
    const request = depositsRequest.current;
    if (!depositPartnerID || !invoice?.businessID) {
      setOpenDeposits([]);
      return;
    }
    try {
      const { items } = await listDeposits({
        businessID: invoice.businessID,
        partnerID: depositPartnerID,
        status: "open",
      });
      if (request === depositsRequest.current) {
        setOpenDeposits(items.filter(isDepositOpen));
      }
    } catch (error) {
      // The box is an offer, not the invoice: without it the drawer still works.
      if (request === depositsRequest.current) setOpenDeposits([]);
    }
  };

  useEffect(() => {
    if (open) loadOpenDeposits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, depositPartnerID, invoice?.businessID]);

  const availableDeposit = openDeposits.reduce(
    (sum, deposit) => sum + (deposit.remaining || 0),
    0,
  );

  const refreshInvoice = async () => {
    if (!invoice?.id) return;
    try {
      const refreshed = await refreshAccountingTransaction(dispatch, invoice.id);
      if (refreshed) {
        setFreshInvoice(refreshed);
        if (onPaySuccess) onPaySuccess(refreshed);
      }
    } catch (error) {
      // The list refresh on close catches up.
    }
  };

  const handleDepositsApplied = async (results, _invoice, { partial } = {}) => {
    setAppliedUsages((prev) => [...prev, ...results]);
    setUndoError("");
    if (!partial) setIsApplyOpen(false);
    await Promise.all([refreshInvoice(), loadOpenDeposits()]);
  };

  // W7 · Batalkan undoes every application made from this drawer.
  const undoAppliedDeposits = async () => {
    if (undoBusy) return;
    setUndoBusy(true);
    setUndoError("");
    const left = [];
    let failure = null;
    for (const usage of appliedUsages) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await cancelDepositUsage(usage.usageId);
      } catch (error) {
        left.push(usage);
        failure = failure || error;
      }
    }
    setAppliedUsages(left);
    setUndoBusy(false);
    if (failure) {
      setUndoError(getDepositActionError(failure, t));
    } else {
      addLocalNotification(notificationDispatch, {
        id: `undo-deposit-${invoice?.id}-${Date.now()}`,
        type: "success",
        title: "common.success",
        body: "deposits.notifications.usageCancelled",
        showFull: true,
      });
    }
    await Promise.all([refreshInvoice(), loadOpenDeposits()]);
  };

  const {
    minDate,
    isDateClosed,
    isClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(invoice?.businessID, { locale: i18n.language });

  // Two independent locks. Editing the invoice is governed by the invoice's own
  // date; recording a payment is governed by the payment's date, which the user
  // is free to set to today even when the invoice itself sits in a closed period.
  const invoiceLocked = isDateClosed(invoice?.createdAt);
  const paymentDateLocked = isDateClosed(paymentDate);

  // The chart is fetched with loadAll, which pages through the list and leaves
  // `accounts` empty between pages — both flags must be clear before an empty
  // chart may be treated as a real answer.
  const accountsLoading = accountLoading || allAccountLoading;

  // category lookup by account id (from the loaded chart of accounts).
  const accountCategoryById = useMemo(() => {
    const map = {};
    (accounts || []).forEach((a) => {
      map[a.id] = a.category;
    });
    return map;
  }, [accounts]);

  // The control account a payment settles: AR (110100) for a sale, AP (220100)
  // for a purchase. Resolved by category so it never grabs the COGS/inventory
  // debit leg (the old "first debit > 0" heuristic did).
  const controlAccount = useMemo(() => {
    const items = invoice?.accounts?.items || [];
    const wantCategory = isPurchase
      ? "ACCOUNTS_PAYABLE"
      : "ACCOUNTS_RECEIVABLE";
    // Prefer the chart-of-accounts category; fall back to the category the leg
    // itself carries (the legs query selects account.category) so this still
    // resolves correctly before the chart finishes loading.
    const leg =
      items.find(
        (a) => accountCategoryById[a.accountID] === wantCategory,
      ) || items.find((a) => a.account?.category === wantCategory);
    if (leg) {
      return { value: leg.accountID, label: leg.account?.name };
    }
    // Next: the standard AR/AP codes, in case the category is absent but the
    // chart follows the default numbering.
    const wantCode = isPurchase ? "220100" : "110100";
    const byCode = items.find((a) => a.account?.code === wantCode);
    if (byCode) {
      return { value: byCode.accountID, label: byCode.account?.name };
    }
    // Last resort (no category, no code): on a sale the receivable is the debit
    // leg carrying the whole invoice total; on a purchase the payable is the
    // credit leg carrying it. Amount-matching avoids grabbing the COGS debit of
    // a post-COGS sale, which the old "first debit > 0" heuristic did.
    const total = invoice?.total;
    const sideOf = (a) => (isPurchase ? a.credit : a.debit);
    const fallback =
      (total
        ? items.find((a) => Math.round(sideOf(a)) === Math.round(total))
        : null) || items.find((a) => sideOf(a) > 0);
    return fallback
      ? { value: fallback.accountID, label: fallback.account?.name }
      : null;
  }, [invoice, accountCategoryById, isPurchase]);

  // Cash & bank accounts for payment account selector
  const cashBankAccountOptions = useMemo(
    () => getCashBankOptions(accounts),
    [accounts],
  );

  useEffect(() => {
    return () => {
      if (downloadRef.current) {
        downloadRef.current.cancel();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load accounts when payment modal opens. `loadAll` is required: the default
  // page size is 10 accounts (code ASC), which truncates the chart well before
  // Piutang/Hutang Usaha — the control account lookup below then finds nothing
  // and the cash & bank picker is missing most options.
  useEffect(() => {
    if (isPaymentModalOpen && invoice?.businessID) {
      requestListAccount(dispatch, {
        businessID: invoice.businessID,
        loadAll: true,
      });
    }
  }, [isPaymentModalOpen, invoice?.businessID, dispatch]);

  // An invoice line stores one tax relation, so the line alone cannot name a
  // second tax on it — a withheld PPh 23 read "Pajak tidak tersedia (2%)"
  // while the tax was alive and in use. The business's tax list supplies the
  // rest of the names; the terms charged still come from the line.
  const taxDispatch = useBusinessTaxDispatch();
  const { businessTaxes } = useBusinessTaxState();

  useEffect(() => {
    if (invoice?.businessID) {
      // Explicit page and limit: the action defaults to ten per page and
      // stops at the first non-empty one, so a business with more taxes than
      // that would leave the directory short of the very tax we came to name.
      requestListBusinessTax(taxDispatch, {
        page: 1,
        limit: TAX_DIRECTORY_LIMIT,
        businessID: invoice.businessID,
      });
    }
  }, [invoice?.businessID, taxDispatch]);

  const taxDirectory = useMemo(
    () => new Map((businessTaxes || []).map((tax) => [tax.id, tax])),
    [businessTaxes],
  );

  // The posted total is the source of truth for a posted invoice; the line
  // arithmetic here is only a cross-check. See useInvoiceTotals.
  const {
    calculations,
    total: invoiceTotal,
    totalPayment,
    balanceDue,
    settlementAmount,
  } = useInvoiceTotals(invoice, taxDirectory);
  // Same rule as Sisa Pembayaran, so the badge and the Bayar button can never
  // say Belum Lunas next to a Sisa of Rp0. See isTransactionPaid.
  const isPaid = isTransactionPaid(invoice);

  // Pre-fill total paid with the balance still owed on the posted invoice.
  // This used to recompute the total from the lines at today's tax rates,
  // which on an invoice with a withheld tax prefilled the gross amount — more
  // than the receivable, so the backend rejected the payment outright.
  //
  // Only once per opening: the `invoice` object is replaced while the form is
  // open, and re-running on it overwrote a typed partial amount with the full
  // balance — Rp2.000.000 typed, Rp5.450.000 saved, invoice marked Lunas.
  const totalPaidPrefilledRef = useRef(false);
  useEffect(() => {
    if (!isPaymentModalOpen) {
      totalPaidPrefilledRef.current = false;
      return;
    }
    if (invoice && !totalPaidPrefilledRef.current) {
      totalPaidPrefilledRef.current = true;
      setTotalPaid(
        settlementAmount > 0
          ? { floatValue: settlementAmount, value: settlementAmount }
          : "",
      );
    }
  }, [isPaymentModalOpen, invoice, settlementAmount]);

  // The balance can still shrink while the form is open (another payment
  // lands). The typed amount is never rewritten to follow it; instead an
  // amount above the balance is flagged, so the user always sees the number
  // that will be saved.
  const totalExceedsBalance = (totalPaid?.floatValue || 0) > settlementAmount;
  const totalPaidError = (value) => {
    if (!(value?.value > 0)) return t("invoices.payment.errors.totalRequired");
    if ((value?.floatValue || 0) > settlementAmount) {
      return t("invoices.payment.errors.totalExceedsBalance", {
        balance: formatRupiah(settlementAmount),
      });
    }
    return "";
  };

  // Over-balance shows before submit too — it can appear without the user
  // touching the field.
  useEffect(() => {
    if (!submitted && isPaymentModalOpen) {
      setTotalError(totalExceedsBalance ? totalPaidError(totalPaid) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalExceedsBalance, settlementAmount, submitted, isPaymentModalOpen]);

  // Re-validate while submitted
  useEffect(() => {
    if (submitted) {
      setDateError(
        paymentDate ? "" : t("invoices.payment.errors.dateRequired"),
      );
      setTotalError(totalPaidError(totalPaid));
      setSelectedAccountError(
        selectedAccount?.value
          ? ""
          : t("invoices.payment.errors.accountRequired"),
      );
      // Silent while the chart is still loading — `accounts` is empty mid-fetch,
      // which is not the same as "this business has no receivable account".
      setControlAccountError(
        controlAccount?.value || accountsLoading
          ? ""
          : t("invoices.payment.errors.controlAccountMissing"),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentDate,
    totalPaid,
    settlementAmount,
    selectedAccount,
    controlAccount,
    accountsLoading,
    submitted,
    t,
  ]);

  // Handle payment success
  useEffect(() => {
    if (transactionID && isPaymentModalOpen) {
      setIsPaymentModalOpen(false);
      resetPaymentModal();
      clearAccountingTransaction(dispatch);
      addLocalNotification(notificationDispatch, {
        id: `pay-invoice-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "invoices.payment.successMessage",
        showFull: true,
      });
      if (onPaySuccess) onPaySuccess(invoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionID]);

  const resetPaymentModal = () => {
    setPaymentDate(new Date());
    setTotalPaid("");
    setPaymentDescription("");
    setSelectedAccount(null);
    setSubmitted(false);
    setDateError("");
    setTotalError("");
    setSelectedAccountError("");
    setControlAccountError("");
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    resetPaymentModal();
    clearAcctTransactionError(dispatch);
  };

  const validatePayment = () => {
    const isDateValid = Boolean(paymentDate);
    const totalMessage = totalPaidError(totalPaid);
    const isTotalValid = !totalMessage;
    const isAccountValid = Boolean(selectedAccount?.value);
    const isControlValid = Boolean(controlAccount?.value);
    setDateError(isDateValid ? "" : t("invoices.payment.errors.dateRequired"));
    setTotalError(totalMessage);
    setSelectedAccountError(
      isAccountValid ? "" : t("invoices.payment.errors.accountRequired"),
    );
    // Without the control account the payload would carry an empty `accounts`
    // array and the backend rejects it ("Total debit and credit not equal to
    // total transaction"). Fail here with a message the user can act on.
    setControlAccountError(
      isControlValid || accountsLoading
        ? ""
        : t("invoices.payment.errors.controlAccountMissing"),
    );
    return isDateValid && isTotalValid && isAccountValid && isControlValid;
  };

  const handlePayment = () => {
    setSubmitted(true);
    if (!validatePayment()) return;
    if (paymentDateLocked) return;

    const input = {
      created_at: paymentDate.toISOString(),
      total: totalPaid.floatValue,
      accounts: [],
      description: paymentDescription,
      paid: true,
      business_id: invoice.businessID,
      partner_id: invoice.partnerID,
      parent_transaction_id: invoice.id,
      type: isPurchase ? "PAYABLE_PAYMENT" : "RECEIVABLE_PAYMENT",
    };

    // Both legs always post — validatePayment() has already guaranteed the
    // control account resolved, so `accounts` is never empty.
    if (isPurchase) {
      // Pay a Bill: Dr Hutang Usaha (220100) / Cr Kas-Bank.
      input.accounts.push({
        id: controlAccount.value,
        debit: input.total,
        credit: 0,
      });
      input.accounts.push({
        id: selectedAccount.value,
        debit: 0,
        credit: input.total,
      });
    } else {
      // Receive on an invoice: Dr Kas-Bank / Cr Piutang Usaha (110100).
      input.accounts.push({
        id: selectedAccount.value,
        debit: input.total,
        credit: 0,
      });
      input.accounts.push({
        id: controlAccount.value,
        debit: 0,
        credit: input.total,
      });
    }

    requestCreateAcctTransaction(dispatch, { input });
  };

  useEffect(() => {
    if (downloadInvoice?.key) {
      downloadFile(downloadInvoice.key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadInvoice]);

  const onDownloadInvoice = () => {
    if (invoice?.id) {
      requestDownloadInvoice(dispatch, { transactionId: invoice.id });
    }
  };

  const onExportXmlClick = () => {
    setShowXmlModal(true);
  };

  const onSubmitXml = (options) => {
    downloadFakturKeluaranXml(invoice, options);
    setShowXmlModal(false);
  };

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    setDownloadProgress(totalBytes ? transferredBytes / totalBytes : 0);
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: { useAccelerateEndpoint: true },
      });
      window.open(urlResult.url.href);
    } catch (err) {
      setDownloadDocError(err);
    }
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);
      const request = downloadData({
        path: ({ identityId }) => `private/${identityId}/${key}`,
        options: {
          useAccelerateEndpoint: true,
          onProgress: onDownloadProgress,
        },
      });
      downloadRef.current = request;
      const result = await request.result;
      downloadBlob(result.body, getAccountingFileName(key));
      setDownloadDocError(null);
    } catch (err) {
      if (err.message !== "canceled") {
        await handleDownloadError(key);
      }
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
      clearDownloadInvoice(dispatch);
    }
  };

  const handleClose = () => {
    if (downloadRef.current) {
      downloadRef.current.cancel();
    }
    setIsPaymentModalOpen(false);
    resetPaymentModal();
    setDownloadDocError(null);
    clearDownloadInvoice(dispatch);
    setIsApplyOpen(false);
    setAppliedUsages([]);
    setUndoError("");
    setDepositDetailID(null);
    onClose();
  };

  const viewPayment = (payment) => {
    // A deposit application is changed only through its deposit, so it opens
    // Detail Uang Muka rather than the plain transaction detail.
    if (isDepositApplication(payment) && payment.depositID) {
      setDepositDetailID(payment.depositID);
      return;
    }
    setSelectedPayment(payment);
    setIsTransactionDetailOpen(true);
    requestGetAccountingTransaction(dispatch, { id: payment.id });
  };

  return (
    <>
      {/* Detail SlideOver */}
      <SlideOver
        open={
          open &&
          !isPaymentModalOpen &&
          !isTransactionDetailOpen &&
          !isApplyOpen &&
          !depositDetailID
        }
        onClose={handleClose}
        title={isPurchase ? "bills.detail.title" : "invoices.detail.title"}
        actionButton={
          invoice &&
          (invoiceLocked ? (
            <PeriodLockCell />
          ) : (
            onEdit && (
              <button
                type="button"
                className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                onClick={() => onEdit(invoice)}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
            )
          ))
        }
        footer={
          invoice && (
            <div className="flex items-center justify-end gap-3">
              {(downloading || downloadInvoiceLoading) && (
                <div className="flex-1">
                  <p className="text-xs text-gray-500 mb-1">
                    {t("common.downloading")}
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-blue-600 h-1.5 rounded-full transition-all duration-200"
                      style={{
                        width: `${Math.round(downloadProgress * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
              <FormError
                className="flex-1"
                error={downloadInvoiceError || downloadDocError}
              />
              {/* XML e-Faktur export is sales-invoice only. */}
              {!isPurchase && (
                <button
                  type="button"
                  onClick={onExportXmlClick}
                  disabled={!invoice}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                >
                  <DocumentIcon className="h-4 w-4" />
                  {t("invoices.detail.actions.exportXml")}
                </button>
              )}
              <button
                type="button"
                onClick={onDownloadInvoice}
                disabled={downloading || downloadInvoiceLoading}
                className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                <DownloadIcon className="h-4 w-4" />
                {t("common.download")}
              </button>
            </div>
          )
        }
      >
        {invoice ? (
          <div className="space-y-6 mb-6">
            {/* W7 · what was just applied, undoable together */}
            {appliedUsages.length > 0 && (
              <div
                role="status"
                className="rounded-md border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900"
              >
                <div className="flex items-start gap-3">
                  <p className="flex-1">
                    {t("deposits.apply.banner", {
                      interpolation: { escapeValue: false },
                      amount: formatRupiah(
                        appliedUsages.reduce((sum, u) => sum + (u.amount || 0), 0),
                      ),
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={undoAppliedDeposits}
                    disabled={undoBusy}
                    className="font-medium text-purple-800 underline hover:text-purple-950 disabled:opacity-50"
                  >
                    {undoBusy ? t("common.loading") : t("deposits.actions.cancelUsage")}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAppliedUsages([]);
                      setUndoError("");
                    }}
                    className="text-purple-500 hover:text-purple-800"
                  >
                    <span className="sr-only">{t("deposits.apply.dismiss")}</span>
                    <XIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <FormError className="mt-2" error={undoError} />
              </div>
            )}

            {/* Header Summary */}
            <div className="bg-white p-4 shadow sm:rounded-lg sm:p-6 border border-gray-100">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    {t(
                      isPurchase
                        ? "bills.detail.fields.invoiceNumber"
                        : "invoices.detail.fields.invoiceNumber",
                    )}
                  </p>
                  <h3 className="mt-1 text-2xl font-bold text-gray-900">
                    #{invoice.invoiceNumber || "-"}
                  </h3>
                </div>
                <span
                  className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold leading-5 ${
                    isPaid
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {isPaid
                    ? t("transactions.statusPayment.paid")
                    : t("transactions.statusPayment.notPaid")}
                </span>
              </div>

              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 pt-4 border-t border-gray-200">
                {invoice.business?.name && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("invoices.detail.fields.businessName")}
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      {invoice.business.name}
                    </dd>
                    {invoice.business.address && (
                      <dd className="text-xs text-gray-500">
                        {invoice.business.address}
                      </dd>
                    )}
                  </div>
                )}

                {invoice.partner?.name && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t(
                        isPurchase
                          ? "bills.detail.fields.supplier"
                          : "invoices.detail.fields.buyer",
                      )}
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      <button
                        type="button"
                        onClick={() => {
                          navigate(
                            `/v2/accounting/contacts?contactId=${invoice.partnerID}`,
                            { state: { partner: invoice.partner } },
                          );
                          handleClose();
                        }}
                        className="text-blue-600 hover:underline text-left"
                      >
                        {invoice.partner.name}
                      </button>
                    </dd>
                    {invoice.partner.address && (
                      <dd className="text-xs text-gray-500">
                        {invoice.partner.address}
                      </dd>
                    )}
                  </div>
                )}

                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("invoices.fields.date")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDateTime(invoice.createdAt, i18n.language)}
                  </dd>
                </div>

                {invoice.paymentDueAt && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("invoices.fields.dueDate")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {dayjs(invoice.paymentDueAt)
                        .locale(i18n.language)
                        .format("DD MMM YYYY")}
                    </dd>
                  </div>
                )}

                {invoice.vatCreditPeriod && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("bills.detail.fields.vatCreditPeriod")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {dayjs(fromPeriod(invoice.vatCreditPeriod))
                        .locale(i18n.language)
                        .format("MMMM YYYY")}
                    </dd>
                  </div>
                )}

                {invoice.referenceNumber && (
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("invoices.detail.fields.referenceNumber")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {invoice.referenceNumber}
                    </dd>
                  </div>
                )}

                {invoice.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("invoices.detail.fields.message")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                      {invoice.description}
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Items Table */}
            {invoice.items?.items?.length > 0 && (
              <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 sm:px-6 border-b border-gray-100">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    {t("invoices.table.headers.product")}
                  </h4>
                </div>
                <div className="max-w-full overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          #
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t("invoices.table.headers.product")}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {t("invoices.table.headers.quantity")}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {t("invoices.table.headers.price")}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {t("invoices.fields.subtotal")}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {t("invoices.table.headers.tax")}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {t("invoices.fields.taxBase")}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {t("invoices.table.headers.taxTotal")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {invoice.items.items.map((item, index) => {
                        const { product, quantity, tax } = item;
                        // Satu sumber perhitungan dgn ekspor XML.
                        const breakdown = calculateItemTaxBreakdown(
                          item,
                          calculations?.subtotal,
                          calculations?.discount,
                          invoice?.discount?.applyAfterTax || false,
                          taxDirectory,
                          invoice?.taxRounding,
                        );
                        const productPrice = breakdown.price;
                        const productSubtotal = breakdown.itemTotal;
                        const itemTaxBase = breakdown.otherTaxBase;
                        // Every tax on the line, at the terms it was posted
                        // with. A tax the guard hides still has an entry, so
                        // the row names it instead of reading as untaxed.
                        const itemTaxes = breakdown.taxes;

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                              {index + 1}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                              {product?.name && product?.id ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigate(
                                      `/v2/accounting/products?productId=${product.id}`,
                                      { state: { product } },
                                    );
                                    handleClose();
                                  }}
                                  className="text-blue-600 hover:underline text-left"
                                >
                                  {product.name}
                                </button>
                              ) : product?.name ? (
                                product.name
                              ) : item.productID ? (
                                <span className="text-gray-500 italic">
                                  {t("products.unavailable")}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
                              {quantity}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
                              {currencyFormat(productPrice)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
                              {currencyFormat(productSubtotal)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              {itemTaxes.length ? (
                                <div className="flex flex-col gap-0.5">
                                  {itemTaxes.map((entry, entryIndex) => {
                                    // Only the tax the line keeps a relation to
                                    // can be opened; the rest are named from
                                    // the terms frozen on the line.
                                    const linkable =
                                      entry.id && entry.id === tax?.id && tax?.name;
                                    return (
                                      <span
                                        key={entry.id || entryIndex}
                                        className={clsx(
                                          "flex items-center gap-1",
                                          entry.withholding && "text-amber-700",
                                        )}
                                      >
                                        {linkable ? (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              navigate(
                                                `/v2/accounting/taxes?taxId=${tax.id}`,
                                                { state: { tax } },
                                              );
                                              handleClose();
                                            }}
                                            className="text-blue-600 hover:underline text-left"
                                          >
                                            {tax.name}
                                          </button>
                                        ) : (
                                          <span
                                            className={clsx(
                                              !entry.name && "text-gray-500 italic",
                                            )}
                                          >
                                            {entry.name || t("taxes.unavailable")}
                                          </span>
                                        )}
                                        {entry.withholding && (
                                          <span className="rounded bg-amber-100 px-1 text-xs text-amber-900">
                                            {t("taxes.badges.withheld")}
                                          </span>
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
                              {itemTaxes.some((entry) => !entry.withholding)
                                ? currencyFormat(itemTaxBase)
                                : "-"}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right whitespace-nowrap">
                              <div className="flex flex-col gap-0.5">
                                {itemTaxes.map((entry, entryIndex) => (
                                  <span
                                    key={entry.id || entryIndex}
                                    className={clsx(
                                      entry.withholding && "text-amber-700",
                                    )}
                                  >
                                    {entry.withholding ? "−" : ""}
                                    {currencyFormat(entry.total)}
                                  </span>
                                ))}
                                {itemTaxes.length === 0 && currencyFormat(0)}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Totals Summary */}
            {calculations && (
              <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
                <div className="px-4 py-4 sm:px-6 space-y-2">
                  <SummaryRow
                    label={t("invoices.fields.subtotal")}
                    value={calculations.subtotal}
                  />
                  {invoice.discount && calculations.discount > 0 && (
                    <SummaryRow
                      label={t("invoices.fields.discount")}
                      value={-calculations.discount}
                      colorClass="text-green-600"
                    />
                  )}
                  {calculations.taxes?.map((tax) => {
                    const row = describeTaxRow(tax, t);
                    return (
                      <SummaryRow
                        key={tax.id}
                        label={row.label}
                        value={row.value}
                        colorClass={row.withholding ? "text-amber-700" : undefined}
                      />
                    );
                  })}
                  {calculations.roundingAmount ? (
                    <SummaryRow
                      label={t("invoices.fields.rounding")}
                      value={calculations.roundingAmount}
                    />
                  ) : null}
                  <div className="border-t border-gray-200 pt-2">
                    <SummaryRow
                      label={t("invoices.fields.total")}
                      value={invoiceTotal}
                      bold
                    />
                  </div>
                  <SummaryRow
                    label={t("invoices.detail.fields.payment")}
                    value={totalPayment}
                  />
                  <SummaryRow
                    label={t("invoices.detail.fields.balanceDue")}
                    value={balanceDue}
                    bold
                    colorClass={
                      balanceDue > 0 ? "text-red-600" : "text-green-600"
                    }
                  />
                </div>

                {/* W5 · the customer still has deposits */}
                {!isPaid && availableDeposit > 0 && (
                  <div className="px-4 py-3 sm:px-6 border-t border-purple-100 bg-purple-50 text-sm text-purple-900">
                    <p className="font-semibold tabular-nums">
                      {t("deposits.apply.available", {
                        interpolation: { escapeValue: false },
                        amount: formatRupiah(availableDeposit),
                      })}
                    </p>
                    <p className="text-purple-800">
                      {t("deposits.apply.availableFrom", {
                        interpolation: { escapeValue: false },
                        count: openDeposits.length,
                        partner: invoice.partner?.name || "-",
                      })}
                    </p>
                  </div>
                )}

                {/* Pay button; Pakai Uang Muka sits beside it, as both
                    reduce Sisa Pembayaran. */}
                {!isPaid && (
                  <div className="px-4 py-3 sm:px-6 bg-gray-50 border-t border-gray-100 flex gap-3">
                    {availableDeposit > 0 && (
                      <button
                        type="button"
                        onClick={() => setIsApplyOpen(true)}
                        className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-purple-600 shadow-sm text-sm font-medium rounded-md text-purple-700 bg-white hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      >
                        {t("deposits.actions.useDeposit")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsPaymentModalOpen(true)}
                      className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-blue-600 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                    >
                      {t("invoices.detail.actions.pay")}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Payment History */}
            <PaymentHistoryList
              payments={invoice.payments?.items}
              onViewPayment={viewPayment}
            />
          </div>
        ) : null}
      </SlideOver>

      {/* Payment SlideOver */}
      <SlideOver
        open={open && isPaymentModalOpen}
        onClose={handleClose}
        title="invoices.payment.title"
        actionButton={
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={handleClosePaymentModal}
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
        }
        footer={
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs text-gray-500">{t("common.total")}</p>
              <NumericFormat
                value={totalPaid?.floatValue || 0}
                thousandSeparator="."
                decimalSeparator=","
                displayType="text"
                prefix="Rp"
                className="text-sm font-semibold text-gray-900"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClosePaymentModal}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handlePayment}
                disabled={
                  submitLoading || accountsLoading || paymentDateLocked
                }
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                {submitLoading
                  ? t("common.loading")
                  : t("invoices.detail.actions.pay")}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-6 mb-6">
          {/* Invoice summary header */}
          {invoice && (
            <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden px-4 py-3 flex justify-between items-center">
              <div>
                <p className="text-xs text-gray-500 font-medium">
                  {t("invoices.detail.fields.invoiceNumber")}
                </p>
                <p className="text-sm font-medium text-gray-900">
                  #{invoice.invoiceNumber || "-"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 font-medium">
                  {t("invoices.detail.fields.balanceDue")}
                </p>
                <NumericFormat
                  value={balanceDue || 0}
                  thousandSeparator="."
                  decimalSeparator=","
                  displayType="text"
                  prefix="Rp"
                  className="text-sm font-medium text-gray-900"
                />
              </div>
            </div>
          )}

          {/* Form */}
          <div className="bg-white shadow sm:rounded-lg border border-gray-100">
            <div className="flex flex-col gap-4 px-4 py-5 sm:px-6">
              <PeriodClosedNotice
                error={submitError}
                onRefresh={refetchPeriod}
              />

              {!getPeriodClosedError(submitError) && (
                <FormError error={submitError} />
              )}

              <FormError error={controlAccountError} />

              <div>
                <FormDate
                  required
                  label="invoices.payment.fields.date"
                  placeholder={t("invoices.payment.fields.datePlaceholder")}
                  selected={paymentDate}
                  onChange={setPaymentDate}
                  dateFormat="dd/MM/yyyy HH:mm"
                  showTimeInput
                  timeInputLabel={t("common.time")}
                  minDate={minDate}
                  isDateClosed={isDateClosed}
                  error={dateError}
                />
                {isClosed && (
                  <p className="mt-1.5 text-xs text-amber-700">
                    {paymentDateLocked
                      ? t("accounting.period.hints.dateLocked", {
                          date: closedThroughLabel,
                        })
                      : t("accounting.period.banner.closedThrough", {
                          date: closedThroughLabel,
                        })}
                  </p>
                )}
              </div>

              <FormInput
                required
                label="invoices.payment.fields.totalPaid"
                placeholder={t("invoices.payment.fields.totalPaidPlaceholder")}
                value={totalPaid?.value ?? ""}
                numberFormat={{
                  thousandSeparator: ".",
                  decimalSeparator: ",",
                  prefix: "Rp",
                  onValueChange: (val) => setTotalPaid(val),
                }}
                error={totalError}
              />

              <FormSelect
                required
                label="invoices.payment.fields.account"
                options={cashBankAccountOptions}
                value={selectedAccount}
                onChange={setSelectedAccount}
                placeholder="invoices.payment.fields.accountPlaceholder"
                isLoading={accountsLoading}
                disabled={accountsLoading}
                searchable
                error={selectedAccountError}
              />

              <FormInput
                label="invoices.payment.fields.note"
                value={paymentDescription}
                placeholder={t("invoices.payment.fields.notePlaceholder")}
                onChange={(e) => setPaymentDescription(e.target.value)}
                onClear={() => setPaymentDescription("")}
                hint={t("common.optional")}
              />
            </div>
          </div>

          {/* Payment History */}
          <PaymentHistoryList
            payments={invoice?.payments?.items}
            onViewPayment={viewPayment}
          />
        </div>
      </SlideOver>

      {isTransactionDetailOpen && (
        <TransactionDetailModal
          open={isTransactionDetailOpen}
          onClose={() => {
            setIsTransactionDetailOpen(false);
            setSelectedPayment(null);
            handleClose();
          }}
          transaction={
            fetchedTransaction?.id === selectedPayment?.id
              ? fetchedTransaction
              : selectedPayment
          }
          parentTransaction={invoice}
          parentTransactionLoading={transactionDetailLoading}
          onViewParentInvoice={() => {
            setIsTransactionDetailOpen(false);
            setIsPaymentModalOpen(false);
            resetPaymentModal();
          }}
        />
      )}
      {/* W6 · Pakai Uang Muka, in place of the detail like Bayar */}
      {invoice && !isPurchase && (
        <DepositApplyPanel
          open={open && isApplyOpen}
          onBack={() => setIsApplyOpen(false)}
          onClose={handleClose}
          invoice={invoice}
          businessID={invoice.businessID}
          onApplied={handleDepositsApplied}
        />
      )}

      {depositDetailID && (
        <DepositDetailModal
          open={open && Boolean(depositDetailID)}
          depositID={depositDetailID}
          businessID={invoice?.businessID}
          onClose={() => setDepositDetailID(null)}
          onChanged={() => {
            refreshInvoice();
            loadOpenDeposits();
          }}
        />
      )}

      {showXmlModal && (
        <FakturXmlExportModal
          open={showXmlModal}
          onClose={() => setShowXmlModal(false)}
          onSubmit={onSubmitXml}
          transaction={invoice}
        />
      )}
    </>
  );
};

export default InvoiceDetailModal;
